import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import jwt_decode from "jwt-decode";
import {
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { CheckCircleOutline, HighlightOff, RadioButtonUnchecked } from "@material-ui/icons";
import qlearnLogo from "../../images/logo_light.png";
import blurImg from "../../images/blurred-exp.png";
import {
  buildPathWithAttribution,
  getAttributionParams,
  persistAttribution,
  readStoredAttribution,
} from "../../utils/attribution";
import { gtmTrack } from "../../utils/tracking";
import "./daily-absite-question.scss";

const googleLogo = require("../../images/google-icon.png").default;

const ANSWERS = [
  {
    id: "laparotomy",
    label: "Perform exploratory laparotomy",
    isCorrect: true,
  },
  {
    id: "ct",
    label: "Obtain CT abdomen and pelvis",
    isCorrect: false,
  },
  {
    id: "observe",
    label: "Observe with serial abdominal exams",
    isCorrect: false,
  },
];

const SELECTED_ANSWER_STORAGE_KEY = "qlearn_daily_absite_selected_answer_id";
const DAILY_ABSITE_PATH = "/daily-absite-question";
const BRIEF_EXPLANATION = "Because this patient is unstable and FAST is positive, the next step is exploratory laparotomy.";
const FULL_EXPLANATION = "Hemodynamic instability with a positive FAST after penetrating trauma is an operative abdomen. CT is for stable patients. Continued resuscitation without source control delays definitive hemorrhage control.";

const getAnswerById = (answerId) => ANSWERS.find((answer) => answer.id === answerId) || null;

const isSignedIn = (storage = typeof window !== "undefined" ? window.localStorage : undefined) => {
  const token = storage?.getItem("token");
  if (!token) {
    return false;
  }

  try {
    const decodedToken = jwt_decode(token);
    if (decodedToken?.exp && Date.now() >= decodedToken.exp * 1000) {
      return false;
    }
  } catch (_) {
    return false;
  }

  return storage?.getItem("isProfileComplete") !== "false";
};

const getInitialSelectedAnswer = () => {
  if (!isSignedIn()) {
    return null;
  }

  try {
    return getAnswerById(window.localStorage.getItem(SELECTED_ANSWER_STORAGE_KEY));
  } catch (_) {
    return null;
  }
};

const buildEventPayload = (storedAttribution, extra = {}) => {
  const params = getAttributionParams(storedAttribution);
  const touch = storedAttribution?.latestTouch || storedAttribution?.firstTouch;

  return {
    exam_type: "ABSITE",
    landing_path: touch?.landingPath,
    utm_source: params.utm_source,
    utm_medium: params.utm_medium,
    utm_campaign: params.utm_campaign,
    utm_content: params.utm_content,
    utm_term: params.utm_term,
    google_click_id: params.gclid,
    facebook_click_id: params.fbclid,
    platform: params.platform,
    ...extra,
  };
};

export default function DailyAbsiteQuestionPage() {
  const [storedAttribution, setStoredAttribution] = useState(() => readStoredAttribution());
  const [selectedAnswer, setSelectedAnswer] = useState(getInitialSelectedAnswer);
  const [signedIn] = useState(() => isSignedIn());
  const [unlockMethod, setUnlockMethod] = useState("google");
  const explanationRef = useRef(null);

  useEffect(() => {
    const attribution = persistAttribution();
    setStoredAttribution(attribution);
    gtmTrack("daily_absite_question_viewed", buildEventPayload(attribution));
  }, []);

  const googleUnlockPath = useMemo(() => (
    buildPathWithAttribution("/login", {
      attributionParams: getAttributionParams(storedAttribution),
      extraParams: {
        auth: "google",
        next: DAILY_ABSITE_PATH,
      },
    })
  ), [storedAttribution]);
  const phoneUnlockPath = useMemo(() => (
    buildPathWithAttribution("/login", {
      attributionParams: getAttributionParams(storedAttribution),
      extraParams: {
        auth: "phone",
        next: DAILY_ABSITE_PATH,
      },
    })
  ), [storedAttribution]);

  const handleAnswerClick = (answer) => {
    const isFirstAnswer = !selectedAnswer;

    setSelectedAnswer(answer);
    window.requestAnimationFrame?.(() => {
      if (typeof explanationRef.current?.scrollIntoView === "function") {
        explanationRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    });

    try {
      window.localStorage.setItem(SELECTED_ANSWER_STORAGE_KEY, answer.id);
    } catch (_) {
      // Remembering the teaser answer is helpful, but should never break the page.
    }

    if (!isFirstAnswer) {
      return;
    }

    gtmTrack("daily_absite_question_answered", buildEventPayload(storedAttribution, {
      answer_id: answer.id,
      answer_state: answer.isCorrect ? "correct" : "incorrect",
    }));
  };

  const primaryCtaPath = "/create-quiz";
  const showPrimaryCta = signedIn;
  const unlockPath = unlockMethod === "phone" ? phoneUnlockPath : googleUnlockPath;

  const handleSignupIntent = () => {
    if (signedIn) {
      return;
    }

    gtmTrack("daily_absite_signup_intent", buildEventPayload(storedAttribution, {
      cta_target: "/login",
    }));
  };

  return (
    <Box className="daily-absite-page">
      <Box className="daily-absite-shell">
        <Box className="daily-absite-header">
          <Box className="daily-absite-identity">
            <Box className="daily-absite-logo-mark">
              <img
                alt="QLearn"
                className="daily-absite-logo"
                src={qlearnLogo}
              />
            </Box>
            <Box className="daily-absite-identity-copy">
              <Typography className="daily-absite-identity-title" variant="subtitle2">
                Medlearnity QLearn
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="daily-absite-layout">
          <Box className="daily-absite-copy">
            <Typography component="h1" variant="h3">
              Daily ABSITE Case Review
            </Typography>
            <Typography className="daily-absite-subtitle" variant="h6">
              One free ABSITE case. Answer, unlock the rationale, then keep practicing.
            </Typography>
          </Box>

          <Paper className="daily-absite-question" elevation={2}>
            <Box className="daily-absite-question-inner">
              <Box className="daily-absite-question-meta">
                <Typography className="daily-absite-kicker" variant="overline">
                  Today&apos;s ABSITE Question
                </Typography>
              </Box>
              <Typography component="h2" variant="h5">
                A trauma patient arrives hypotensive after a penetrating abdominal injury. FAST is positive despite resuscitation. What is the next best step?
              </Typography>

              <Box className="daily-absite-answers">
                {ANSWERS.map((answer) => {
                  const isSelected = selectedAnswer?.id === answer.id;
                  // Once the learner has answered, always reveal the correct
                  // option — even when they picked a wrong one — so a miss
                  // still teaches which answer was right (they shouldn't have
                  // to infer it from the explanation prose).
                  const revealAsCorrect = Boolean(selectedAnswer) && answer.isCorrect;
                  let stateClassName = "";
                  if (isSelected) {
                    stateClassName = `selected ${answer.isCorrect ? "correct" : "incorrect"}`;
                  } else if (revealAsCorrect) {
                    stateClassName = "revealed correct";
                  }
                  const answerIcon = isSelected
                    ? (answer.isCorrect ? <CheckCircleOutline /> : <HighlightOff />)
                    : (revealAsCorrect ? <CheckCircleOutline /> : <RadioButtonUnchecked />);

                  return (
                    <Button
                      className={`daily-absite-answer ${stateClassName}`}
                      disableRipple
                      key={answer.id}
                      onClick={() => handleAnswerClick(answer)}
                      startIcon={answerIcon}
                      variant="outlined"
                    >
                      {answer.label}
                      {revealAsCorrect && !isSelected && (
                        <span className="daily-absite-sr-only"> (correct answer)</span>
                      )}
                      {isSelected && (
                        <span className="daily-absite-sr-only"> (your answer)</span>
                      )}
                    </Button>
                  );
                })}
              </Box>

              {selectedAnswer && (
                <Box className="daily-absite-explanation" ref={explanationRef}>
                  <Box className="daily-absite-explanation-result" role="status">
                    <Typography variant="subtitle2">
                      {selectedAnswer.isCorrect ? "Correct" : "Not quite"}
                    </Typography>
                  </Box>
                  <Box
                    className="daily-absite-brief-explanation"
                    data-testid="daily-absite-brief-explanation"
                  >
                    <Typography variant="body2">
                      {BRIEF_EXPLANATION}
                    </Typography>
                  </Box>
                  {signedIn ? (
                    <Box className="daily-absite-full-explanation">
                      <Typography variant="body2">
                        {FULL_EXPLANATION}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      className="daily-absite-locked-explanation"
                      data-testid="daily-absite-locked-explanation"
                    >
                      <Box
                        aria-hidden="true"
                        className="daily-absite-blurred-preview"
                        data-testid="daily-absite-blurred-preview"
                      >
                        <img alt="" className="daily-absite-blurred-preview-image" src={blurImg} />
                      </Box>
                      <Box className="daily-absite-qlink-login-box">
                        <Typography className="daily-absite-unlock-title" variant="subtitle1">
                          Unlock the full rationale
                        </Typography>
                        <Typography className="daily-absite-unlock-copy" variant="body2">
                          Sign in free to unlock the teaching point and keep practicing.
                        </Typography>
                        <Tabs
                          className="daily-absite-unlock-tabs"
                          centered
                          indicatorColor="primary"
                          onChange={(_, nextMethod) => setUnlockMethod(nextMethod)}
                          textColor="primary"
                          value={unlockMethod}
                        >
                          <Tab label="Google Sign In" value="google" />
                          <Tab label="Phone Sign In" value="phone" />
                        </Tabs>
                        <Button
                          aria-label={unlockMethod === "google" ? "Sign In with Google to unlock explanation" : "Sign In with Phone to unlock explanation"}
                          className={unlockMethod === "google" ? "daily-absite-google-unlock" : "daily-absite-phone-unlock"}
                          color={unlockMethod === "google" ? "secondary" : "primary"}
                          component={Link}
                          onClick={handleSignupIntent}
                          to={unlockPath}
                          variant={unlockMethod === "google" ? "outlined" : "contained"}
                        >
                          {unlockMethod === "google" && (
                            <img
                              alt=""
                              aria-hidden="true"
                              className="daily-absite-google-icon"
                              src={googleLogo}
                            />
                          )}
                          {unlockMethod === "google" ? "Sign In with Google" : "Sign In with Phone"}
                        </Button>
                        <Typography className="daily-absite-legal-copy" variant="caption">
                          By signing in, you agree to QLearn&apos;s{" "}
                          <Link to="/terms">Terms</Link>
                          {" "}and{" "}
                          <Link to="/privacy-policy">Privacy Policy</Link>
                          .
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {showPrimaryCta && (
                <Button
                  className="daily-absite-primary-cta"
                  color="primary"
                  component={Link}
                  onClick={handleSignupIntent}
                  to={primaryCtaPath}
                  variant="contained"
                >
                  Start Practicing
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
